import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleCallback: (data) => api.post('/auth/google/callback', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  deleteAccount: () => api.delete('/auth/account'),
};

// Clients APIs
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getProjects: (id) => api.get(`/clients/${id}/projects`),
  getSummary: (id) => api.get(`/clients/${id}/summary`),
};

// Projects APIs
export const projectsAPI = {
  getAll: (status) => api.get('/projects', { params: { status } }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getSummary: (id) => api.get(`/projects/${id}/summary`),
};

// Intakes APIs
export const intakesAPI = {
  getAll: (stage) => api.get('/intakes', { params: { stage } }),
  get: (id) => api.get(`/intakes/${id}`),
  create: (data) => api.post('/intakes', data),
  update: (id, data) => api.put(`/intakes/${id}`, data),
  delete: (id) => api.delete(`/intakes/${id}`),
};

// Transactions APIs
export const transactionsAPI = {
  getAll: (type) => api.get('/transactions', { params: { type } }),
  create: (data) => api.post('/transactions', data),
  delete: (id) => api.delete(`/transactions/${id}`),
  getSummary: () => api.get('/finances/summary'),
};

// Events APIs
export const eventsAPI = {
  getAll: (start, end) => api.get('/events', { params: { start, end } }),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
};

// Time Entries APIs
export const timeEntriesAPI = {
  getAll: (projectId) => api.get('/time-entries', { params: { project_id: projectId } }),
  create: (data) => api.post('/time-entries', data),
  delete: (id) => api.delete(`/time-entries/${id}`),
};

// Expense Entries APIs
export const expenseEntriesAPI = {
  getAll: (projectId) => api.get('/expense-entries', { params: { project_id: projectId } }),
  create: (data) => api.post('/expense-entries', data),
  delete: (id) => api.delete(`/expense-entries/${id}`),
};

// Invoices APIs
export const invoicesAPI = {
  getAll: (status) => api.get('/invoices', { params: { status } }),
  get: (id) => api.get(`/invoices/${id}`),
  getDetails: (id) => api.get(`/invoices/${id}/details`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// Tasks APIs
export const tasksAPI = {
  getAll: (projectId, status) => api.get('/tasks', { params: { project_id: projectId, status } }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Photos APIs
export const photosAPI = {
  getByProject: (projectId) => api.get(`/projects/${projectId}/photos`),
  upload: (projectId, photoType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/photos?photo_type=${photoType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (projectId, photoId) => api.delete(`/projects/${projectId}/photos/${photoId}`),
  getFileUrl: (storagePath) => {
    const token = localStorage.getItem('token');
    const baseUrl = api.defaults.baseURL;
    return `${baseUrl}/files/${storagePath}?auth=${token}`;
  }
};

// Intake Reminders
export const remindersAPI = {
  getUpcoming: () => api.get('/intakes/reminders'),
};

export default api;
